/**
 * JWT Token Utilities
 * Handles parsing and expiration checking for JWT tokens
 */

/**
 * Parse JWT and extract expiration time (in seconds since epoch)
 */
export const parseTokenExpiration = (token: string | undefined): number | null => {
  if (!token) return null
  try {
    // JWT format: header.payload.signature
    // Remove 'Bearer ' prefix if present
    const cleanToken = token.startsWith('Bearer ') ? token.slice(7) : token
    const payload = cleanToken.split('.')[1]
    if (!payload) return null
    const decoded = JSON.parse(atob(payload))
    return decoded.exp || null
  } catch {
    return null
  }
}

/**
 * Creates a token expiration manager with caching
 */
export const createTokenExpirationManager = () => {
  let cachedExpiration: number | null = null

  return {
    /**
     * Update cached expiration from a token
     */
    updateFromToken: (token: string | undefined) => {
      cachedExpiration = parseTokenExpiration(token)
    },

    /**
     * Get the cached expiration timestamp
     */
    getExpiration: () => cachedExpiration,

    /**
     * Clear the cached expiration
     */
    clear: () => {
      cachedExpiration = null
    },

    /**
     * Check if token expires within given seconds (default 5 minutes = 300 seconds)
     */
    isExpiringSoon: (withinSeconds = 300, nowFn = () => Math.floor(Date.now() / 1000)): boolean => {
      if (!cachedExpiration) return true // No expiration = treat as expired
      const now = nowFn()
      return cachedExpiration - now <= withinSeconds
    },

    /**
     * Check if token is already expired
     */
    isExpired: (nowFn = () => Math.floor(Date.now() / 1000)): boolean => {
      if (!cachedExpiration) return true
      const now = nowFn()
      return cachedExpiration <= now
    },
  }
}

/**
 * Create a JWT token for testing purposes
 */
export const createTestToken = (exp: number): string => {
  const header = btoa(JSON.stringify({ alg: 'HS256', typ: 'JWT' }))
  const payload = btoa(JSON.stringify({ exp, sub: 'test-user' }))
  const signature = 'test-signature'
  return `${header}.${payload}.${signature}`
}
